import React, { useState } from "react"
import { useSprings, animated, to } from "react-spring"
import { useDrag } from "react-use-gesture"

import Card from "../card/Card"

import style from "./CardSwiper.module.scss"

// https://github.com/react-spring/react-use-gesture/blob/master/examples/src/cards/index.tsx
const SPRINGS = (i: number) => ({
  x: 0,
  y: i * -4,
  scale: 1,
  rot: -10 + Math.random() * 20,
  delay: i * 100,
})
const trans = (r: number, s: number) =>
  `rotateX(5deg) rotateY(${r / 10}deg) rotateZ(${r}deg) scale(${s})`
const cards = ["start", "card1", "card2", "card3", "card4", "card5", "cta"]

const CardSwiper: React.FC = () => {
  const [gone] = useState(() => new Set<number>())
  const [springProps, set] = useSprings(cards.length, i => ({ ...SPRINGS(i) }))

  const bind = useDrag(({ args: [index], down, movement: [mx], direction: [xDir], velocity }) => {
    const trigger = velocity > 0.2 // If you flick hard enough it should trigger the card to fly out
    const dir = xDir < 0 ? -1 : 1 // Direction should either point left or right
    if (!down && trigger) gone.add(index) // If button/finger's up and trigger velocity is reached, we flag the card ready to fly out
    set((i: number) => {
      if (index !== i) return null // We're only interested in changing spring-data for the current spring
      const isGone = gone.has(index)
      const x = isGone ? (200 + window.innerWidth) * dir : down ? mx : 0 // When a card is gone it flys out left or right, otherwise goes back to zero
      const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0) // How much the card tilts, flicking it harder makes it rotate faster
      const scale = down ? 1.1 : 1 // Active cards lift up a bit
      const config = { friction: 50, tension: down ? 800 : isGone ? 200 : 500 }
      return { x, rot, scale, config }
    })

    if (!down && gone.size === cards.length)
      setTimeout(() => void gone.clear() || set(i => SPRINGS(i)), 600)
  })

  return (
    <div className={style.container}>
      {springProps.map(({ x, y, rot, scale }, i) => (
        <animated.div key={i} className={style.card} style={{ x, y }}>
          <animated.div {...bind(i)} style={{ transform: to([rot, scale], trans) }}>
            <Card index={i} />
          </animated.div>
        </animated.div>
      ))}
    </div>
  )
}

export default CardSwiper
